import { type DestinationStream, pino, stdSerializers, type LoggerOptions, type Logger as PinoLogger } from 'pino';
import {
  type LambdaContext,
  type LambdaEvent,
  lambdaRequestTracker,
  pinoLambdaDestination,
  StructuredLogFormatter,
} from 'pino-lambda';
import { z } from 'zod';

export type Logger = PinoLogger;

export const getLogger = (options: LoggerOptions = {}, dest?: DestinationStream) =>
  pino(
    {
      ...options,
      serializers: {
        ...options.serializers,
        err: (error: unknown) => {
          if (error instanceof z.ZodError) {
            return z.treeifyError(error);
          }

          if (options.serializers?.['err']) {
            return options.serializers['err'](error);
          }

          if (error instanceof Error) {
            return stdSerializers.err(error);
          }

          return error;
        },
      },
      level: options.level || process.env['LOG_LEVEL'] || 'info',
      base: {
        ...((process.env['DATAPLANE_VERSION'] || process.env['DPQA_CLOUD_VERSION']) && {
          version: process.env['DATAPLANE_VERSION'] || process.env['DPQA_CLOUD_VERSION'],
        }),
        ...options.base,
      },
    },
    dest || pinoLambdaDestination({
      formatter: new StructuredLogFormatter(), // to ensure JSON format
    }),
  );

export const withRequest = (event: LambdaEvent, ctx: LambdaContext) => {
  const tracker = lambdaRequestTracker({
    requestMixin: (e: LambdaEvent, _ctx: LambdaContext): Record<string, string | undefined> => ({
      tenant: e['requestContext']?.['authorizer']?.['tenant'],
      user: e['requestContext']?.['authorizer']?.['userId'],
      functionName: process.env['AWS_LAMBDA_FUNCTION_NAME'],
    }),
  });

  tracker(event, ctx);
};
